/** Signing in with a mobile number and a code sent by text: the steps both apps share. */
import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from "react";
import logo from "../../../../brand/logo.svg";
import type { WebApp } from "../app";
import { errorMessage, post } from "./api";
import { phone as formatPhone } from "./format";
import { ChevronLeft } from "./icons";
import { refreshPushRegistration } from "./push";
import type { OtpChallenge, SignedIn } from "./types";
import { Button, IconButton } from "./ui";

/** The firm's full logo (brand/logo-full.svg), if it has one. */
const fullLogo = Object.values(
  import.meta.glob<string>("../../../../brand/logo-full.svg", {
    eager: true,
    query: "?url",
    import: "default",
  }),
)[0];

/** The number as the person will recognise it. */
export function displayPhone(input: string): string {
  const digits = input.replace(/[^\d+]/g, "");
  if (digits.startsWith("+44")) return formatPhone(digits);
  if (digits.startsWith("07") && digits.length === 11) return formatPhone(`+44${digits.slice(1)}`);
  return input.trim();
}

export function PhoneStep({
  app,
  hero,
  footer,
  onSent,
}: {
  app: WebApp;
  /** The heading and words above the number. */
  hero: ReactNode;
  /** Under the button, e.g. the terms. */
  footer?: ReactNode;
  onSent: (phone: string, challenge: OtpChallenge) => void;
}) {
  const [phone, setPhone] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const id = useId();

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const challenge = await post<OtpChallenge>("/auth/otp/request", { phone, app });
      onSent(phone, challenge);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="sign-in-form" onSubmit={submit}>
      <div className="sign-in-hero">{hero}</div>
      <div className="phone-group">
        <label htmlFor={id} className="sr-only">
          Mobile number
        </label>
        <div className="phone-field">
          <span className="phone-field-prefix" aria-hidden="true">
            <UkFlag /> +44
          </span>
          <input
            id={id}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="07700 900123"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            aria-invalid={error ? true : undefined}
            required
            // biome-ignore lint/a11y/noAutofocus: signing in is all this screen is for.
            autoFocus
          />
        </div>
        {error && <p className="field-error">{error}</p>}
      </div>
      <Button type="submit" loading={busy} disabled={phone.replace(/\D/g, "").length < 10}>
        Send code
      </Button>
      <div className="sign-in-spacer" />
      {footer}
    </form>
  );
}

export function CodeStep({
  app,
  phone,
  challenge,
  sentAt,
  onBack,
  onResent,
  onSignedIn,
  help,
}: {
  app: WebApp;
  phone: string;
  challenge: OtpChallenge;
  sentAt: number;
  onBack: () => void;
  onResent: (challenge: OtpChallenge) => void;
  /** With the new session. Throw to show an error instead. */
  onSignedIn: (session: SignedIn) => Promise<void> | void;
  /** Under the buttons, e.g. why a code might not come. */
  help?: ReactNode;
}) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const submitted = useRef<string | null>(null);
  const codeInput = useRef<HTMLInputElement>(null);

  // Straight on to typing the code (or letting the phone fill it in).
  useEffect(() => codeInput.current?.focus(), []);

  // Android's Chrome can read the code from the text, after asking (WebOTP).
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per code sent.
  useEffect(() => {
    if (!("OTPCredential" in window)) return;
    const abort = new AbortController();
    navigator.credentials
      .get({ otp: { transport: ["sms"] }, signal: abort.signal } as CredentialRequestOptions)
      .then((credential) => {
        const value = (credential as { code?: string } | null)?.code?.replace(/\D/g, "");
        if (value?.length === 6) {
          setCode(value);
          void verify(value);
        }
      })
      .catch(() => undefined);
    return () => abort.abort();
  }, [challenge.challengeId]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const wait = Math.max(0, Math.ceil((sentAt + challenge.resendAfterSeconds * 1000 - now) / 1000));

  async function verify(value: string) {
    if (submitted.current === value) return;
    submitted.current = value;
    setBusy(true);
    setError(null);
    try {
      const session = await post<SignedIn>("/auth/otp/verify", {
        challengeId: challenge.challengeId,
        code: value,
      });
      void refreshPushRegistration(app).catch(() => undefined);
      await onSignedIn(session);
    } catch (err) {
      setError(errorMessage(err));
      setCode("");
      submitted.current = null;
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setError(null);
    try {
      onResent(await post<OtpChallenge>("/auth/otp/request", { phone, app }));
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  return (
    <form
      className="sign-in-form"
      onSubmit={(event) => {
        event.preventDefault();
        void verify(code);
      }}
    >
      <IconButton label="Change number" onClick={onBack} className="sign-in-back">
        <ChevronLeft />
      </IconButton>
      <div className="sign-in-hero">
        <h1>Enter your code</h1>
        <p>
          We've texted a 6-digit code to <strong>{displayPhone(phone)}</strong>.
        </p>
      </div>
      <div className="code-boxes">
        {Array.from({ length: 6 }, (_, i) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: one box per digit, always six.
            key={i}
            className={`code-box${code[i] ? " filled" : ""}${i === Math.min(code.length, 5) ? " current" : ""}`}
            aria-hidden="true"
          >
            {code[i] ?? ""}
          </span>
        ))}
        <input
          className="code-input"
          aria-label="6-digit code"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d{6}"
          maxLength={6}
          value={code}
          ref={codeInput}
          disabled={busy}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, "").slice(0, 6);
            setCode(value);
            if (value.length === 6) void verify(value);
          }}
        />
      </div>
      {error && <p className="field-error center">{error}</p>}
      <Button type="submit" loading={busy} disabled={code.length !== 6}>
        Continue
      </Button>
      <Button variant="ghost" disabled={wait > 0} onClick={resend}>
        {wait > 0 ? `Send a new code in ${wait}s` : "Send a new code"}
      </Button>
      {help}
    </form>
  );
}

function UkFlag() {
  return (
    <svg width="22" height="15" viewBox="0 0 60 40" aria-hidden="true" style={{ borderRadius: 3 }}>
      <clipPath id="uk-flag">
        <rect width="60" height="40" rx="4" />
      </clipPath>
      <g clipPath="url(#uk-flag)">
        <rect width="60" height="40" fill="#012169" />
        <path d="M0 0l60 40M60 0L0 40" stroke="#fff" strokeWidth="8" />
        <path d="M0 0l60 40M60 0L0 40" stroke="#C8102E" strokeWidth="3" />
        <path d="M30 0v40M0 20h60" stroke="#fff" strokeWidth="12" />
        <path d="M30 0v40M0 20h60" stroke="#C8102E" strokeWidth="7" />
      </g>
    </svg>
  );
}

/**
 * The top of the sign-in screens: the firm's full logo if it has one, otherwise its logo over
 * a drawing of a trip from A to B.
 */
export function BrandArt({ firmName }: { firmName: string }) {
  if (fullLogo) return <img src={fullLogo} alt={firmName} className="sign-in-hero-art" />;
  return (
    <>
      <img src={logo} alt={firmName} className="sign-in-logo" />
      <RouteArt />
    </>
  );
}

/**
 * A trip from A to B over a few streets, in the firm's colour: the picture on the sign-in
 * screens of firms without a full logo.
 */
export function RouteArt() {
  const route = "M44 128 C 96 128, 92 70, 150 72 S 228 34, 290 30";
  return (
    <svg className="sign-in-art" viewBox="0 0 340 160" aria-hidden="true">
      <defs>
        <linearGradient id="route-art-line" x1="0" x2="1">
          <stop offset="0" stopColor="currentColor" />
          <stop offset="1" stopColor="var(--text)" />
        </linearGradient>
        <filter id="route-art-glow" x="-20%" y="-50%" width="140%" height="200%">
          <feGaussianBlur stdDeviation="6" />
        </filter>
      </defs>
      <g stroke="var(--border-strong)" strokeWidth="1.2" fill="none">
        <path d="M0 98h340M0 52h340M0 140h340M70 0v160M190 0v160M262 0v160M128 0v160" />
        <path d="M0 18l100 60M210 160l130-80" opacity="0.7" />
      </g>
      <path
        d={route}
        fill="none"
        stroke="currentColor"
        strokeWidth="10"
        opacity="0.45"
        filter="url(#route-art-glow)"
      />
      <path
        d={route}
        fill="none"
        stroke="url(#route-art-line)"
        strokeWidth="4.5"
        strokeLinecap="round"
      />
      <circle cx="44" cy="128" r="16" fill="currentColor" opacity="0.18" className="pulse" />
      <circle cx="44" cy="128" r="9.5" fill="var(--bg)" stroke="currentColor" strokeWidth="5.5" />
      <rect x="281" y="21" width="18" height="18" rx="4" fill="var(--text)" />
      <rect x="287" y="27" width="6" height="6" rx="1" fill="var(--bg)" />
    </svg>
  );
}
